import type { HTMLAttributes, KeyboardEvent, MouseEvent } from "react";
import { cn } from "@/common/lib/utils/utils";

const interactiveElementSelector = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "[contenteditable='true']",
  "[role='button']",
  "[role='link']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const focusClassName =
  "cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring";

type ClickableRowRole = "button" | "link";

interface ClickableRowOptions {
  className?: string;
  role?: ClickableRowRole;
  /**
   * Keep native TR/TD semantics for data tables. Pointer activation remains,
   * but the row is not exposed as a link/button (keyboard uses cell controls).
   */
  preserveTableSemantics?: boolean;
}

function isFromInteractiveChild(
  event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>,
): boolean {
  const target = event.target;

  if (!(target instanceof Element) || target === event.currentTarget) {
    return false;
  }

  const interactiveElement = target.closest(interactiveElementSelector);
  return (
    interactiveElement !== null && interactiveElement !== event.currentTarget
  );
}

export function getClickableRowProps<T extends HTMLElement>(
  onActivate: (event: MouseEvent<T> | KeyboardEvent<T>) => void,
  {
    className,
    role = "link",
    preserveTableSemantics = false,
  }: ClickableRowOptions = {},
): Pick<
  HTMLAttributes<T>,
  "className" | "onClick" | "onKeyDown" | "role" | "tabIndex"
> {
  const onClick: HTMLAttributes<T>["onClick"] = (event) => {
    if (!isFromInteractiveChild(event)) {
      onActivate(event);
    }
  };

  if (preserveTableSemantics) {
    return {
      className: cn("cursor-pointer", className),
      onClick,
    };
  }

  return {
    className: cn(focusClassName, className),
    role,
    tabIndex: 0,
    onClick,
    onKeyDown: (event) => {
      if (
        !isFromInteractiveChild(event) &&
        (event.key === "Enter" || event.key === " ")
      ) {
        event.preventDefault();
        onActivate(event);
      }
    },
  };
}
